import { Box, IconButton, Typography, useTheme, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import ExoAvatar from '../exo/ExoAvatar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GroupIcon from '@mui/icons-material/Group';
import TagIcon from '@mui/icons-material/Tag';
import ArchiveIcon from '@mui/icons-material/Archive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PushPinIcon from '@mui/icons-material/PushPin';
import InfoIcon from '@mui/icons-material/Info';
import { useState } from 'react';
import { useChatClient } from './useChatClient';
import { API_PATCH, DBRequest, useConfig } from '../../api/api';
import { useTranslation } from 'react-i18next';

export const ChatHeader = ({ conversation, onBack, onInfoClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();
  const { refreshConversations, setSelectedConversation, updateConversation } = useChatClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionPending, setActionPending] = useState(null); // 'pin' | 'mute' | 'archive' | null

  const isMuted = conversation.currentUserParticipant?.mutedUntil &&
    new Date(conversation.currentUserParticipant.mutedUntil) > new Date();

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleArchive = () => {
    if (actionPending) return;
    setActionPending('archive');
    DBRequest({
      config,
      path: `chat/conversations/${conversation.id}/archive`,
      method: API_PATCH({}),
      onResponse: (res) => {
        setActionPending(null);
        if (res.data?.isArchived) {
          setSelectedConversation(null);
        }
        refreshConversations();
      },
      onError: () => setActionPending(null),
    });
    handleMenuClose();
  };

  const handleMute = () => {
    if (actionPending) return;
    setActionPending('mute');
    const duration = isMuted ? 0 : 1440;

    DBRequest({
      config,
      path: `chat/conversations/${conversation.id}/mute`,
      method: API_PATCH({ duration }),
      onResponse: (res) => {
        setActionPending(null);
        updateConversation(conversation.id, {
          currentUserParticipant: {
            ...conversation.currentUserParticipant,
            mutedUntil: res.mutedUntil,
          },
        });
      },
      onError: () => setActionPending(null),
    });
    handleMenuClose();
  };

  const handlePin = () => {
    if (actionPending) return;
    setActionPending('pin');
    DBRequest({
      config,
      path: `chat/conversations/${conversation.id}/pin`,
      method: API_PATCH({}),
      onResponse: (res) => {
        setActionPending(null);
        updateConversation(conversation.id, {
          currentUserParticipant: {
            ...conversation.currentUserParticipant,
            isPinned: res.isPinned,
          },
        });
        refreshConversations();
      },
      onError: () => setActionPending(null),
    });
    handleMenuClose();
  };

  const getConversationIcon = () => {
    if (conversation.type === 'direct') {
      const otherParticipant = conversation.participants?.find(
        p => p.userId !== conversation.currentUserParticipant?.userId
      );
      // Picture is in user.picture (from UserResource via ChatParticipantResource)
      const pictureUrl = otherParticipant?.user?.picture?.temporaryUrl || otherParticipant?.user?.picture?.url;
      return (
        <ExoAvatar
          size="36px"
          name={conversation.name || t('User')}
          picture={pictureUrl}
        />
      );
    }

    if (conversation.type === 'channel') {
      return (
        <Box
          className="flex items-center justify-center rounded-xl"
          sx={{
            width: 36,
            height: 36,
            backgroundColor: colors.bgInput,
          }}
        >
          <TagIcon sx={{ color: colors.info, fontSize: 18 }} />
        </Box>
      );
    }

    return (
      <Box
        className="flex items-center justify-center rounded-xl"
        sx={{
          width: 36,
          height: 36,
          backgroundColor: colors.bgInput,
        }}
      >
        <GroupIcon sx={{ color: colors.info, fontSize: 18 }} />
      </Box>
    );
  };

  const getSubtitle = () => {
    if (conversation.type === 'direct') {
      return t('Direct Message');
    }
    const count = conversation.participants?.length || 0;
    return `${count} ${t('participants')}`;
  };

  return (
    <Box className="flex items-center justify-between px-4 py-3">
      <Box className="flex items-center gap-3">
        <IconButton
          onClick={onBack}
          size="small"
          sx={{
            display: { xs: 'flex', md: 'none' },
            color: colors.text,
            opacity: 0.7,
            '&:hover': { opacity: 1 },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </IconButton>

        {getConversationIcon()}

        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ color: colors.text }}>
            {conversation.name || t('Chat')}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.text, opacity: 0.5, fontSize: '0.7rem' }}>
            {getSubtitle()}
          </Typography>
        </Box>
      </Box>

      <Box className="flex items-center gap-1">
        <IconButton
          onClick={onInfoClick}
          size="small"
          sx={{ color: colors.text, opacity: 0.5, '&:hover': { opacity: 1 } }}
        >
          <InfoIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <IconButton
          onClick={handleMenuOpen}
          size="small"
          sx={{ color: colors.text, opacity: 0.5, '&:hover': { opacity: 1 } }}
        >
          <MoreVertIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: colors.glass,
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            minWidth: 180,
          },
        }}
      >
        <MenuItem onClick={handlePin} disabled={!!actionPending} sx={{ borderRadius: '8px', mx: 0.5 }}>
          <ListItemIcon>
            <PushPinIcon fontSize="small" sx={{ color: colors.text, opacity: 0.7 }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
            {conversation.currentUserParticipant?.isPinned ? t('Unpin') : t('Pin')}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMute} disabled={!!actionPending} sx={{ borderRadius: '8px', mx: 0.5 }}>
          <ListItemIcon>
            {isMuted ? (
              <NotificationsIcon fontSize="small" sx={{ color: colors.text, opacity: 0.7 }} />
            ) : (
              <NotificationsOffIcon fontSize="small" sx={{ color: colors.text, opacity: 0.7 }} />
            )}
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
            {isMuted ? t('Unmute') : t('Mute')}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleArchive} disabled={!!actionPending} sx={{ borderRadius: '8px', mx: 0.5 }}>
          <ListItemIcon>
            <ArchiveIcon fontSize="small" sx={{ color: colors.text, opacity: 0.7 }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
            {conversation.isArchived ? t('Unarchive') : t('Archive')}
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};
